import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  useNotificationsSummary,
  useNotificationsList,
  useMarkNotificationRead,
} from '@api/queries';
import { notifTargetRoute } from '@auth/notifRoute';
import styles from './Header.module.css';

export default function Header({ onMenuClick }) {
  // In a real build this comes from auth context.
  const user = { name: 'Dr Devi Prasad Rao', role: 'Super Admin', initials: 'DD' };

  const navigate = useNavigate();

  // Real unread count from the notifications API
  const { data: notifSummary } = useNotificationsSummary();
  const notificationCount = notifSummary?.unread ?? 0;

  // Dropdown state + preview list (top 5)
  const [notifOpen, setNotifOpen] = useState(false);
  // Load the list only when the dropdown is open; the badge uses the summary.
  const { data: notifPages } = useNotificationsList({}, { enabled: notifOpen });
  const recentNotifications = (notifPages?.pages?.[0]?.notifications || []).slice(0, 5);

  const markRead = useMarkNotificationRead();

  // Close dropdown on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setNotifOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('1ayurved_token');
    localStorage.removeItem('1ayurved_user');
    window.location.href = '/';
  };

  const toggleNotif = () => setNotifOpen((v) => !v);

  const handleNotifItemClick = (n) => {
    setNotifOpen(false);
    if (!n.readAt) markRead.mutate(n.id);
    // Role-aware target so a Super Admin's bell never bounces to /clinics on a
    // clinic/doctor event's route (QA NOT-001). null → stay put.
    const target = notifTargetRoute(n);
    if (target) navigate(target);
  };

  const handleSeeAll = () => {
    setNotifOpen(false);
    navigate('/notifications');
  };

  return (
    <header className={styles.header}>
      <button type="button" className={styles.hamburger} onClick={onMenuClick} aria-label="Open menu">
        <i className="bi bi-list" aria-hidden="true" />
      </button>
      <div className={styles.brand}>
        <Link to="/" className={styles.brandLink} aria-label="1Ayurved — Home">
          <img
            src="/brand/logo.webp"
            alt="1Ayurved — Repair, Revive, Rejuvenate"
            className={styles.brandLogo}
          />
        </Link>
      </div>

      <div className={styles.right}>
        {/* ── Notifications bell ────────────────────────────────── */}
        <div className={styles.notifWrap}>
          <button
            type="button"
            className={`${styles.bell} ${notifOpen ? styles.bellActive : ''}`}
            onClick={toggleNotif}
            aria-label={`Notifications${notificationCount > 0 ? ` (${notificationCount} unread)` : ''}`}
          >
            <i className="bi bi-bell" aria-hidden="true" />
            {notificationCount > 0 && (
              <span className={styles.bellBadge}>
                {notificationCount > 99 ? '99+' : notificationCount}
              </span>
            )}
          </button>

          {notifOpen && (
            <>
              <div className={styles.backdrop} onClick={() => setNotifOpen(false)} />
              <div className={styles.notifDropdown}>
                <div className={styles.notifDropdownHead}>
                  <div className={styles.notifDropdownTitle}>Notifications</div>
                  {notificationCount > 0 && (
                    <div className={styles.notifDropdownCount}>{notificationCount} unread</div>
                  )}
                </div>

                <div className={styles.notifDropdownList}>
                  {recentNotifications.length === 0 ? (
                    <div className={styles.notifDropdownEmpty}>
                      <div className={styles.notifDropdownEmptyIcon}><i className="bi bi-bell-slash" aria-hidden="true" /></div>
                      <div>No notifications yet</div>
                    </div>
                  ) : (
                    recentNotifications.map((n) => (
                      <button
                        key={n.id}
                        type="button"
                        className={`${styles.notifDropdownItem} ${!n.readAt ? styles.notifDropdownItemUnread : ''}`}
                        onClick={() => handleNotifItemClick(n)}
                      >
                        <span className={styles.notifDropdownItemIcon}>
                          <i className={`bi ${iconForKind(n.kind)}`} aria-hidden="true" />
                        </span>
                        <div className={styles.notifDropdownItemMain}>
                          <div className={styles.notifDropdownItemTitle}>
                            {n.title}
                            {n.severity === 'critical' && (
                              <span className={styles.notifDropdownItemCriticalDot} />
                            )}
                          </div>
                          <div className={styles.notifDropdownItemMeta}>
                            {n.clinic || 'Network'} · {formatRelative(n.createdAt)}
                          </div>
                        </div>
                        {!n.readAt && <span className={styles.notifDropdownItemUnreadDot} />}
                      </button>
                    ))
                  )}
                </div>

                <button
                  type="button"
                  className={styles.notifDropdownFooter}
                  onClick={handleSeeAll}
                >
                  See all notifications →
                </button>
              </div>
            </>
          )}
        </div>

        {/* ── User block ───────────────────────────────────────── */}
        <div className={styles.user}>
          <div className={styles.userChip}>
            <div className={styles.avatar} aria-hidden="true">{user.initials}</div>
            <div className={styles.userMeta}>
              <div className={styles.userName}>{user.name}</div>
              <div className={styles.userRole}>{user.role}</div>
            </div>
          </div>
          <button className={styles.logout} onClick={handleLogout} type="button" title="Logout" aria-label="Logout">
            <i className="bi bi-box-arrow-right" aria-hidden="true" />
            <span className={styles.logoutText}>Logout</span>
          </button>
        </div>
      </div>
    </header>
  );
}

/* ──────────────────────────── Helpers ──────────────────────────── */

function iconForKind(kind) {
  switch (kind) {
    case 'compliance':       return 'bi-calendar-check';
    case 'data-correction':  return 'bi-tools';
    case 'inventory':        return 'bi-capsule';
    case 'system':           return 'bi-gear';
    case 'clinical':         return 'bi-heart-pulse';
    default:                 return 'bi-megaphone';
  }
}

function formatRelative(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  const min = Math.floor(diffMs / 60_000);
  if (min < 1)   return 'just now';
  if (min < 60)  return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24)   return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 7)   return `${day}d`;
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}